import { JobApplication, api } from '@common';
import { useAuth } from '@hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

// Interface for creating a new application
interface CreateJobApplicationInput {
  fullName: string;
  email: string;
  phone: string;
  positionId: string;
  message: string;
}

// Interface for status updates
interface UpdateJobApplicationStatusInput {
  status: 'pending' | 'reviewed' | 'contacted' | 'rejected' | 'hired';
  adminComments?: string;
}

// Interface para parâmetros de busca
interface JobApplicationsParams {
  page?: number;
  limit?: number;
  search?: string;
  department?: string;
  orderBy?: string;
  orderType?: 'asc' | 'desc';
}

export const getJobApplicationsWithFilters = (params: JobApplicationsParams) => {
  const { isAuthenticated } = useAuth();

  return useQuery<JobApplication[]>({
    queryKey: ['jobApplications', params],
    queryFn: async () => {
      try {
        const response = await api.get('/job-applications', {
          params: {
            ...params,
            include: 'position'
          }
        });

        let data = response.data;
        if (data && typeof data === 'object' && !Array.isArray(data) && 'data' in data) {
          data = data.data;
        }

        if (!Array.isArray(data)) {
          console.error('API não retornou um array válido:', data);
          return [];
        }

        return data;
      } catch (error) {
        console.error('Error retrieving applications:', error);
        throw error;
      }
    },
    enabled: isAuthenticated
  });
};

export const useJobApplications = () => {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  const jobApplications = useQuery<JobApplication[]>({
    queryKey: ['jobApplications'],
    queryFn: async () => {
      return api.get('/job-applications', {
        params: {
          include: 'position'
        }
      })
        .then((response) => response.data)
        .catch((error) => {
          message.error('Error retrieving applications');
          throw error;
        });
    },
    enabled: isAuthenticated // Só executa a query se o usuário estiver autenticado
  });

  const getJobApplicationsByPositionId = (positionId: string) => {
    return useQuery<JobApplication[]>({
      queryKey: ['jobApplications', 'position', positionId],
      queryFn: async () => {
        return api.get(`/job-applications/position/${positionId}`, {
          params: {
            include: 'position'
          }
        })
          .then((response) => response.data)
          .catch((error) => {
            message.error('Error retrieving applications for this position');
            throw error;
          });
      },
      enabled: !!positionId && isAuthenticated

    });
  };

  const getJobApplicationById = (id: string) => {
    return useQuery<JobApplication>({
      queryKey: ['jobApplication', id],
      queryFn: async () => {
        return api.get(`/job-applications/${id}`, {
          params: {
            include: 'position'
          }
        })
          .then((response) => response.data)
          .catch((error) => {
            message.error('Error retrieving application details');
            throw error;
          });
      },
      enabled: !!id && isAuthenticated
    });
  };

  const createJobApplication = useMutation({
    mutationFn: async ({ data, resume }: { data: CreateJobApplicationInput; resume?: File }) => {
      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        formData.append(key, value);
      });

      if (resume) {
        formData.append('resume', resume);
      }

      try {
        const response = await api.post('/job-applications', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      } catch (error: any) {
        console.error('API error submitting application:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobApplications'] });
    }
  });

  const updateJobApplicationStatus = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateJobApplicationStatusInput }) => {
      return api.patch(`/job-applications/${id}/status`, data)
        .then((response) => response.data)
        .catch((error) => {
          console.error('API error updating application status:', error);
          message.error('Error updating application status');
          throw error;
        });
    },
    onSuccess: (_data, variables) => {
      message.success('Application status updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['jobApplications'] });
      queryClient.invalidateQueries({ queryKey: ['jobApplication', variables.id] });
      queryClient.invalidateQueries({
        queryKey: ['jobApplications', 'position'],
        exact: false
      });
    }
  });

  const deleteJobApplication = useMutation({
    mutationFn: (id: string) => {
      return api.delete(`/job-applications/${id}`)
        .then((response) => response.data)
        .catch((error) => {
          message.error('Error deleting application');
          throw error;
        });
    },
    onSuccess: (_, id) => {
      message.success('Application deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['jobApplications'] });
      queryClient.invalidateQueries({ queryKey: ['jobApplication', id] });
      queryClient.invalidateQueries({
        queryKey: ['jobApplications', 'position'],
        exact: false
      });
    }
  });

  // Função para gerar URL do currículo
  const getResumeUrl = (resume: string) => {
    return `${import.meta.env.VITE_URL_FILES}/${resume}`;
  };

  return {
    jobApplications,
    getJobApplicationsWithFilters,
    getJobApplicationsByPositionId,
    getJobApplicationById,
    createJobApplication,
    updateJobApplicationStatus,
    deleteJobApplication,
    getResumeUrl
  };
};
